import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  {
    id: 1,
    text: 'Right now, which statement feels most true?',
    options: [
      { label: 'I can barely get through the day without falling apart', score: 1 },
      { label: 'I am functional but constantly asking myself why this keeps happening', score: 2 },
      { label: 'I know what happened but I am struggling to find myself again', score: 3 },
      { label: 'I have done the healing work and I am ready to build what is next', score: 4 },
    ],
  },
  {
    id: 2,
    text: 'When you think about the relationship or situation that brought you here, what comes up first?',
    options: [
      { label: 'Physical sensations — chest tightness, nausea, inability to sleep', score: 1 },
      { label: 'Confusion and pattern analysis — trying to understand what happened', score: 2 },
      { label: 'A sense of having lost yourself somewhere along the way', score: 3 },
      { label: 'Clarity about the past and uncertainty about the future', score: 4 },
    ],
  },
  {
    id: 3,
    text: 'How would you describe your nervous system right now?',
    options: [
      { label: 'Constantly activated — I am either in panic or completely shut down', score: 1 },
      { label: 'Mostly regulated but certain triggers send me into a spiral', score: 2 },
      { label: 'Generally stable but I struggle with self-trust and boundaries', score: 3 },
      { label: 'Regulated and ready to focus on building forward', score: 4 },
    ],
  },
  {
    id: 4,
    text: 'Which of these feels like your most urgent need?',
    options: [
      { label: 'Something to help me stop the spiral right now', score: 1 },
      { label: 'Understanding why I keep ending up in the same patterns', score: 2 },
      { label: 'Rebuilding my sense of who I actually am', score: 3 },
      { label: 'A clear path forward for my life and relationships', score: 4 },
    ],
  },
  {
    id: 5,
    text: 'When you are activated or triggered, what typically happens?',
    options: [
      { label: 'I completely lose my ability to function or think clearly', score: 1 },
      { label: 'I can function but I obsessively analyze what happened', score: 2 },
      { label: 'I withdraw and struggle to trust my own instincts', score: 3 },
      { label: 'I notice it happening and can usually work through it', score: 4 },
    ],
  },
  {
    id: 6,
    text: 'How clear are you on your own values, needs, and non-negotiables?',
    options: [
      { label: 'I have no idea — I have been living for everyone else\'s needs', score: 1 },
      { label: 'I have some sense but it gets confused when I am triggered', score: 2 },
      { label: 'I know what they are but struggle to honor them consistently', score: 3 },
      { label: 'I am clear on them and ready to build my life around them', score: 4 },
    ],
  },
  {
    id: 7,
    text: 'Which statement best describes your relationship with yourself right now?',
    options: [
      { label: 'I do not recognize myself — I have completely lost who I was', score: 1 },
      { label: 'I understand myself better but still feel controlled by old patterns', score: 2 },
      { label: 'I am reconnecting with myself but need to rebuild self-trust', score: 3 },
      { label: 'I have a strong sense of self and I am ready to lead my own life', score: 4 },
    ],
  },
  {
    id: 8,
    text: 'What does your support system look like right now?',
    options: [
      { label: 'I feel completely alone and do not know who to turn to', score: 1 },
      { label: 'I have people around me but feel like a burden to them', score: 2 },
      { label: 'I have support but the healing feels lonely and internal', score: 3 },
      { label: 'I have solid support and I am ready to expand my world', score: 4 },
    ],
  },
  {
    id: 9,
    text: 'How are you sleeping and caring for your basic physical needs?',
    options: [
      { label: 'Poorly — sleep is disrupted, appetite is off, body feels dysregulated', score: 1 },
      { label: 'Inconsistently — some days better than others', score: 2 },
      { label: 'Generally okay but stress affects my routines', score: 3 },
      { label: 'Well — I have stable routines and my body feels grounded', score: 4 },
    ],
  },
  {
    id: 10,
    text: 'What does healing look like to you right now?',
    options: [
      { label: 'Surviving today without completely falling apart', score: 1 },
      { label: 'Finally understanding why I keep repeating the same patterns', score: 2 },
      { label: 'Getting back to the person I was before I lost myself', score: 3 },
      { label: 'Building a life that is fully and intentionally mine', score: 4 },
    ],
  },
];

export const AssessmentPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const nextButtonLabel = currentQuestion === questions.length - 1 ? 'Show me my healing path' : 'Next';

  const handleNext = () => {
    if (selectedOption === null) {
      toast.error('Please select an answer');
      return;
    }

    const newAnswers = [
      ...answers,
      {
        question_id: questions[currentQuestion].id,
        question: questions[currentQuestion].text,
        score: selectedOption,
      },
    ];

    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null); // Clear selection AFTER moving to next question
    } else {
      submitAssessment(newAnswers);
    }
  };

  const submitAssessment = async (finalAnswers) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/assessment/submit`,
        { answers: finalAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await refreshUser();
      navigate('/phase-assignment', { state: { result: response.data } });
    } catch (error) {
      toast.error('Failed to submit assessment');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-stone-500">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <span className="text-sm text-stone-500">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="assessment-progress" />
        </div>

        <div
          className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 md:p-10"
          data-testid="assessment-question-card"
        >
          <h2
            className="text-2xl md:text-3xl font-medium mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {questions[currentQuestion].text}
          </h2>

          <RadioGroup 
            key={currentQuestion}
            value={selectedOption !== null ? selectedOption.toString() : undefined} 
            onValueChange={(val) => setSelectedOption(parseInt(val))}
          >
            <div className="space-y-4">
              {questions[currentQuestion].options.map((option, idx) => (
                <Label
                  key={option.label || `option-${idx}`}
                  htmlFor={`option-${idx}`}
                  className="flex items-center space-x-3 p-4 rounded-xl border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer"
                >
                  <RadioGroupItem value={option.score.toString()} id={`option-${idx}`} />
                  <span className="flex-1 text-base">{option.label}</span>
                </Label>
              ))}
            </div>
          </RadioGroup>

          <div className="flex justify-between mt-8">
            <Button
              onClick={() => {
                if (currentQuestion > 0) {
                  setCurrentQuestion(currentQuestion - 1);
                  setAnswers(answers.slice(0, -1));
                  setSelectedOption(null);
                }
              }}
              disabled={currentQuestion === 0}
              variant="ghost"
              className="rounded-full"
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading}
              className="rounded-full px-8 bg-stone-800 text-stone-50 hover:bg-stone-700"
              data-testid="assessment-next-button"
            >
              {loading ? 'Submitting...' : nextButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
